import classNames from 'classnames';
import styled from 'styled-components';

import { RosterDay } from './types';

import { ORANGE, PRIMARY } from 'constants/colors';

import { getCellDateClasses } from './utils';
import { formatByLocale, getDSTDiff } from 'utils/date';

import TableCell from '@mui/material/TableCell';
import Tooltip from '@mui/material/Tooltip';

import { Icon } from '@sdb/planning-ui';

const HeaderTableCellStyles = styled(TableCell)`
  position: relative;

  .date-label {
    border-radius: 50%;
    width: 36px;
    height: 36px;
    display: inline-flex;
    justify-content: center;
    align-items: center;
  }

  &.is-month-view {
    .date-label {
      width: 26px;
      height: 26px;
    }
  }

  &.is-today,
  &.is-holiday {
    font-family: 'SourceSansPro-Bold';

    .date-label {
      background-color: ${PRIMARY};
      color: white;
      cursor: pointer;
    }
  }

  &.is-holiday {
    .date-label {
      background-color: ${ORANGE} !important;
    }
  }

  &.is-weekend {
    font-family: 'SourceSansPro-Bold';
  }

  .dst-icon {
    font-size: 14px;
    vertical-align: middle;

    &.reverse {
      transform: translateX(-50%) scaleX(-1);
    }
  }
`;

type Props = {
  children?: React.ReactNode;
  rosterDay: RosterDay;
  view?: 'week' | 'month';
};

export default function HeaderTableCell({ children, rosterDay, view }: Props) {
  const { date, isToday, isHoliday, description } = rosterDay;
  const DSTDiff = getDSTDiff(date);
  const isMonthView = view === 'month';

  const dateLabel = (
    <div className="date-label">
      {isMonthView ? date.getDate() : formatByLocale(date, 'd-M')}
    </div>
  );

  return (
    <HeaderTableCellStyles
      className={getCellDateClasses({
        date,
        isHoliday,
        view
      })}
    >
      <div>
        {Boolean(DSTDiff) && (
          <Icon
            type="clock-rotate-left"
            fontSize="small"
            className={classNames('dst-icon', { reverse: DSTDiff === 1 })}
          />
        )}
        {formatByLocale(date, isMonthView ? 'EEEEEE' : 'EEEE')}
      </div>
      {(isHoliday || isToday) && (
        <Tooltip
          title={description}
          arrow
          enterDelay={300}
          enterNextDelay={300}
        >
          {dateLabel}
        </Tooltip>
      )}
      {!(isHoliday || isToday) && dateLabel}
      {children}
    </HeaderTableCellStyles>
  );
}
